/**
 * Kasa Hareketleri — SALT-OKUNUR kasa defteri / denetim ekranı.
 *
 * Her kuruşun izi: satış, açılış, kapanış, gider, çekme/ekleme, bahşiş.
 * Veri GİRİŞİ buradan YAPILMAZ — nakit gider tek kapıdan (Gelir-Gider ekranı)
 * girilir, oradan kasaya köprülenir. Bu ekran sadece gösterir (kasa defteri
 * mantığı: defter işlemi yansıtır, üretmez).
 *
 * Varsayılan: sadece AÇIK kasanın hareketleri. Denetim için "Kapalı kasa
 * günleri dahil" seçilebilir.
 */
import {
  Card, Stack, Table, Badge, Loader, Center, Group, Select, SimpleGrid,
  Switch, Alert, Button, Modal, NumberInput, Divider, Text, Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconArrowDown, IconArrowUp, IconReceipt, IconAlertTriangle, IconCheck,
  IconCashRegister, IconCalendar,
} from "@tabler/icons-react";
import { useState } from "react";
import { notifications } from "@mantine/notifications";
import { useResource, useShiftXReport, useCloseShift, useOpenShift } from "../../../shared/api";
import { PageHeader, MoneyDisplay, EmptyState, KPICard, formatDateTime } from "../../../shared/ui";

/** Date → "YYYY-MM-DD" (lokal alanlardan; toISOString UTC'ye kaydırıp yanlış
 *  güne düşürmesin). Backend bu günü Europe/Istanbul gün sınırı olarak yorumlar. */
function toLocalDateStr(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** "...(order #bd58c516-cb13-...)" → ilk 8 hane; reference_id öncelikli, regex fallback. */
const UUID_RE = /([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
function shortNoteCode(notes: string, referenceId?: string): string | null {
  if (referenceId) return referenceId.slice(0, 8);
  const m = notes?.match(UUID_RE);
  return m ? m[1] : null;
}
/** Notes metnindeki tam UUID'yi ilk-8 ile değiştir (gösterim için). */
function shortenNote(notes: string, referenceId?: string): string {
  if (!notes) return "-";
  const short = shortNoteCode(notes, referenceId);
  if (!short) return notes;
  return notes.replace(UUID_RE, `#${short}`);
}

/** Açık kasanın yaşı: "17 saat 43 dakikadır açık". */
function shiftAge(openedAt: string): { text: string; hours: number } {
  const ms = Date.now() - new Date(openedAt).getTime();
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  const text = hours > 0
    ? `${hours} saat ${mins} dakikadır açık`
    : `${mins} dakikadır açık`;
  return { text, hours };
}

const MOVEMENT_COLOR: Record<string, string> = {
  OPENING_FLOAT: "blue",
  SALE: "green",
  REFUND: "red",
  TIP: "yellow",
  DEPOSIT: "teal",
  WITHDRAWAL: "orange",
  EXPENSE: "violet",
  RESERVATION_DEPOSIT: "grape",
  RESERVATION_REFUND: "pink",
  CLOSING_COUNT: "gray",
  ADJUSTMENT: "gray",
  CASH_VARIANCE: "red",
};

const MOVEMENT_LABEL: Record<string, string> = {
  OPENING_FLOAT: "Açılış Nakit",
  SALE: "Satış",
  REFUND: "İade",
  TIP: "Bahşiş",
  DEPOSIT: "Kasaya Para Konuldu",
  WITHDRAWAL: "Kasadan Para Çıkışı",
  EXPENSE: "Gider",
  RESERVATION_DEPOSIT: "Rezervasyon Avansı",
  RESERVATION_REFUND: "Rezervasyon İadesi",
  CLOSING_COUNT: "Kapanış Sayımı",
  ADJUSTMENT: "Düzeltme",
  CASH_VARIANCE: "Kasa Farkı",
};

export function CashMovementsPage() {
  // Varsayılan: sadece açık kasa. Denetim için kapalı günler de açılabilir.
  const [showClosed, setShowClosed] = useState(false);
  // Kasa günü seçici tarih aralığı. Şube başına günde 1 kasa açıldığı için
  // yıllar geçince shift sayısı PAGE_SIZE'ı (20) aşar; aralık seçmeden dropdown
  // yalnız en YENİ 20 günü gösterir (eskiler sessizce gizli kalırdı). Aralık
  // seçilince backend opened_at'a göre daraltır → istenen gün mutlaka listede.
  const [shiftRange, setShiftRange] = useState<[Date | null, Date | null]>([null, null]);
  // Şube başına günde 1 kasa → aralık genişliği ≈ kasa günü sayısı. Backend
  // PAGE_SIZE=20 olduğundan 20 günden geniş aralıkta dropdown yine kesilir
  // (sessiz kayıp). Bu yüzden aralığı 20 GÜNLE SINIRLA: fazlası seçilirse
  // filtreyi UYGULAMA ve kullanıcıyı uyar → dropdown hiçbir zaman kesilmez.
  const RANGE_MAX_DAYS = 20;
  const rangeDays =
    shiftRange[0] && shiftRange[1]
      ? Math.floor((shiftRange[1].getTime() - shiftRange[0].getTime()) / 86_400_000) + 1
      : 0;
  const rangeTooWide = rangeDays > RANGE_MAX_DAYS;
  const shiftFilters: Record<string, any> = showClosed ? {} : { closed_at__isnull: true };
  // Geçersiz (çok geniş) aralık backend'e gitmesin — eski, kesilmiş listeye
  // düşmektense filtreyi hiç uygulama.
  if (!rangeTooWide) {
    if (shiftRange[0]) shiftFilters.date_from = toLocalDateStr(shiftRange[0]);
    if (shiftRange[1]) shiftFilters.date_to = toLocalDateStr(shiftRange[1]);
  }
  const { data: shiftsData } = useResource<any>("shifts", { filters: shiftFilters });
  const shifts: any[] = shiftsData?.results || shiftsData || [];
  const [shiftId, setShiftId] = useState<string | null>(null);
  const [closeShiftId, setCloseShiftId] = useState<string | null>(null);
  const [openModalOpen, setOpenModalOpen] = useState(false);

  // Açık kasa banner'ı her durumda görünsün diye ayrı sorgu (showClosed=true
  // olsa bile açık kasayı uyar).
  const { data: openShiftsData } = useResource<any>("shifts", {
    filters: { closed_at__isnull: true },
  });
  const openShifts: any[] =
    openShiftsData?.results || openShiftsData || [];

  const { data: movData, isLoading } = useResource<any>("cash-movements", {
    filters: shiftId ? { shift: shiftId } : (showClosed ? {} : { shift_open: true }),
  });
  const movements: any[] = movData?.results || movData || [];

  if (isLoading) return <Center h={300}><Loader /></Center>;

  // Hızlı özet
  const cashIn = movements.filter((m) => m.amount_minor > 0).reduce((s, m) => s + m.amount_minor, 0);
  const cashOut = movements.filter((m) => m.amount_minor < 0).reduce((s, m) => s + Math.abs(m.amount_minor), 0);
  const tipTotal = movements.filter((m) => m.movement_type === "TIP").reduce((s, m) => s + m.amount_minor, 0);

  return (
    <Stack gap="md">
      <PageHeader
        title="Kasa Hareketleri"
        subtitle="Nakit kasa defteri (salt okunur). Tahsilat detayı için → Ödemeler & İadeler, gider girişi için → Gelir-Gider."
      />

      {/* Açık kasa YOKSA: kasayı açmaya davet (kapatma butonunun simetriği).
          Garson kasa açamaz; OWNER/MANAGER/CASHIER buradan açabilsin. */}
      {openShifts.length === 0 && (
        <Alert color="blue" icon={<IconCashRegister size={18} />} title="Açık kasa yok">
          <Group justify="space-between" align="center">
            <Text size="sm">
              Sipariş alınabilmesi için şubenin kasası açık olmalı. Başlangıç
              nakdini girip kasayı açın.
            </Text>
            <Button size="sm" leftSection={<IconCashRegister size={16} />} onClick={() => setOpenModalOpen(true)}>
              Kasayı Aç
            </Button>
          </Group>
        </Alert>
      )}

      {/* Açık kasa uyarısı — gün sonu unutulmasın (yaş bilgisiyle). */}
      {openShifts.map((s: any) => {
        const age = shiftAge(s.opened_at);
        return (
          <Alert
            key={s.id}
            color={age.hours >= 12 ? "red" : "yellow"}
            icon={<IconAlertTriangle size={18} />}
            title="Açık kasa"
          >
            <Group justify="space-between" align="center">
              <div>
                <Text size="sm">
                  Açılış: {new Date(s.opened_at).toLocaleString("tr-TR")} — {s.user_name || "—"}
                </Text>
                <Text size="sm" fw={700}>{age.text}</Text>
              </div>
              <Button
                size="sm"
                color={age.hours >= 12 ? "red" : "yellow"}
                onClick={() => setCloseShiftId(s.id)}
              >
                Kasayı Kapat
              </Button>
            </Group>
          </Alert>
        );
      })}

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <KPICard title="Toplam Giriş" value={<MoneyDisplay minor={cashIn} />} icon={<IconArrowDown size={20} />} color="green" />
        <KPICard title="Toplam Çıkış" value={<MoneyDisplay minor={cashOut} />} icon={<IconArrowUp size={20} />} color="red" />
        <KPICard title="Bahşiş" value={<MoneyDisplay minor={tipTotal} />} icon={<IconReceipt size={20} />} color="yellow" />
      </SimpleGrid>

      <Group justify="space-between" align="flex-end">
        <Group align="flex-end" gap="sm">
          {/* Önce tarih aralığıyla dönemi daralt (dropdown'ı besler), sonra
              dropdown'dan tek kasayı seç → soldan sağa doğal akış. */}
          <DatePickerInput
            type="range"
            label="Tarih aralığı"
            placeholder="Tüm günler"
            leftSection={<IconCalendar size={16} />}
            value={shiftRange}
            onChange={(v) => setShiftRange(v as [Date | null, Date | null])}
            error={
              rangeTooWide
                ? `En çok ${RANGE_MAX_DAYS} günlük aralık seçilebilir (seçilen: ${rangeDays} gün)`
                : undefined
            }
            clearable
            w={260}
          />
          <Select
            label="Kasa günü filtresi"
            placeholder={showClosed ? "Tüm kasa günleri" : "Açık kasa"}
            // Uzun geçmişte liste PAGE_SIZE ile kesilir; eski günü bulmak için
            // soldaki tarih aralığını kullan (aşağıdaki description bunu söyler).
            description={
              shifts.length >= 20
                ? "Liste en yeni günlerle sınırlı — eski gün için tarih aralığı seç"
                : undefined
            }
            searchable
            nothingFoundMessage="Bu aralıkta kasa günü yok"
            // Açık kasalar başa + 🟢 ile vurgulu; kapalılar 🔒.
            data={[...shifts]
              .sort((a, b) => Number(!!a.closed_at) - Number(!!b.closed_at))
              .map((s: any) => ({
                value: s.id,
                label: `${s.closed_at ? "🔒" : "🟢"} ${new Date(s.opened_at).toLocaleString("tr-TR")}${s.closed_at ? " (kapalı)" : " (AÇIK)"} — ${s.user_name || s.user || "—"}`,
              }))}
            value={shiftId}
            onChange={setShiftId}
            clearable
            w={460}
          />
        </Group>
        <Switch
          label="Kapalı kasa günlerini de göster (denetim)"
          checked={showClosed}
          onChange={(e) => { setShowClosed(e.currentTarget.checked); setShiftId(null); }}
        />
      </Group>

      {movements.length === 0 ? (
        <EmptyState
          title="Kasa hareketi yok"
          description={showClosed ? "Bu kapsamda hareket bulunamadı." : "Açık kasa yok veya henüz hareket girilmemiş."}
        />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Table.ScrollContainer minWidth={720}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Zaman</Table.Th>
                  <Table.Th>Tip</Table.Th>
                  <Table.Th>Kasa</Table.Th>
                  <Table.Th>Referans</Table.Th>
                  <Table.Th>Not</Table.Th>
                  <Table.Th>Kayıt Eden</Table.Th>
                  <Table.Th ta="right">Tutar</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {movements.slice(0, 300).map((m: any) => (
                  <Table.Tr key={m.id}>
                    <Table.Td>{formatDateTime(m.created_at)}</Table.Td>
                    <Table.Td>
                      <Badge color={MOVEMENT_COLOR[m.movement_type] || "gray"}>
                        {MOVEMENT_LABEL[m.movement_type] || m.movement_type}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{m.shift_label || "-"}</Table.Td>
                    <Table.Td>{m.reference_type ? <Badge variant="light" size="sm">{m.reference_type}</Badge> : "-"}</Table.Td>
                    <Table.Td>
                      {m.notes ? (
                        <Tooltip label={m.notes} multiline withArrow>
                          <Text size="sm" span style={{ cursor: "help" }}>
                            {shortenNote(m.notes, m.reference_id)}
                          </Text>
                        </Tooltip>
                      ) : "-"}
                    </Table.Td>
                    <Table.Td>{m.actor_name || "-"}</Table.Td>
                    <Table.Td ta="right" fw={700} c={m.amount_minor >= 0 ? "green" : "red"}>
                      {m.amount_minor >= 0 ? "+" : ""}<MoneyDisplay minor={m.amount_minor} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <AdminShiftCloseModal
        shiftId={closeShiftId}
        onClose={() => setCloseShiftId(null)}
      />

      <AdminShiftOpenModal
        opened={openModalOpen}
        onClose={() => setOpenModalOpen(false)}
      />
    </Stack>
  );
}

/** Admin'in yeni gün kasasını açması — POS'taki Kasa Açılışı ekranının
 *  admin karşılığı. Şube başına tek kasa: backend idempotent (zaten açıksa
 *  mevcudu döner). Başlangıç nakit = OPENING_FLOAT hareketi. */
function AdminShiftOpenModal({
  opened, onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const openShift = useOpenShift();
  const [openingCash, setOpeningCash] = useState<number>(0);

  async function handleOpen() {
    try {
      await openShift.mutateAsync({
        opening_cash_minor: Math.round((openingCash || 0) * 100),
      });
      notifications.show({
        message: "Kasa açıldı.",
        color: "green", icon: <IconCheck size={16} />,
      });
      setOpeningCash(0);
      onClose();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Kasa açılamadı.",
        color: "red",
      });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Kasayı Aç (Gün Başı)" centered>
      <Stack gap="md">
        <Text c="dimmed" size="sm">
          Kasada başlangıç nakit tutarını girin. Şube başına tek kasa açılır;
          zaten açıksa mevcut kasa kullanılır.
        </Text>

        <NumberInput
          label="Başlangıç Nakit (TL)"
          value={openingCash}
          onChange={(v) => setOpeningCash(Number(v) || 0)}
          size="lg"
          min={0}
          decimalScale={2}
          thousandSeparator=","
        />

        <Button
          size="lg"
          fullWidth
          leftSection={<IconCashRegister size={18} />}
          loading={openShift.isPending}
          onClick={handleOpen}
        >
          Kasayı Aç
        </Button>
      </Stack>
    </Modal>
  );
}

/** Admin'in açık kasayı sayıp kapatması — POS modalının aynısı (X raporu +
 *  variance). Kapatan = admin (closed_by), kasayı açan değişmez. */
function AdminShiftCloseModal({
  shiftId, onClose,
}: {
  shiftId: string | null;
  onClose: () => void;
}) {
  const opened = !!shiftId;
  const { data: x, isLoading } = useShiftXReport(shiftId);
  const closeShift = useCloseShift();
  const [counted, setCounted] = useState<number>(0);

  const expectedMinor: number = x?.expected_cash_minor ?? 0;
  const countedMinor = Math.round((counted || 0) * 100);
  const varianceMinor = countedMinor - expectedMinor;

  async function handleClose() {
    if (!shiftId) return;
    try {
      await closeShift.mutateAsync({
        shiftId, counted_cash_minor: countedMinor,
      });
      notifications.show({
        message: "Kasa kapatıldı (gün sonu).",
        color: "green", icon: <IconCheck size={16} />,
      });
      onClose();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        notifications.show({ message: "Bu kasa zaten kapatılmış.", color: "orange" });
        onClose();
      } else {
        notifications.show({
          message: e?.response?.data?.detail || "Kasa kapatılamadı.",
          color: "red",
        });
      }
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Kasayı Kapat (Gün Sonu)" centered>
      <Stack gap="md">
        {isLoading || !x ? (
          <Center h={120}><Loader /></Center>
        ) : (
          <>
            <Card padding="sm" radius="md" withBorder>
              <Stack gap={4}>
                <CloseRow label="Açılış Kasası" minor={x.opening_cash_minor} />
                <CloseRow label="Nakit Satışlar" minor={x.cash_sales_minor} />
                {x.tips_minor > 0 && <CloseRow label="Bahşiş" minor={x.tips_minor} />}
                {x.deposits_minor > 0 && <CloseRow label="Kasaya Eklenen" minor={x.deposits_minor} />}
                {x.cash_refunds_minor > 0 && <CloseRow label="Nakit İadeler" minor={-x.cash_refunds_minor} />}
                {x.expenses_paid_minor > 0 && <CloseRow label="Kasadan Gider" minor={-x.expenses_paid_minor} />}
                {x.withdrawals_minor > 0 && <CloseRow label="Kasadan Çekilen" minor={-x.withdrawals_minor} />}
                <Divider my={4} />
                <Group justify="space-between">
                  <Text fw={700}>Beklenen Kasa</Text>
                  <MoneyDisplay minor={expectedMinor} fw={700} />
                </Group>
              </Stack>
            </Card>

            <NumberInput
              label="Sayılan Nakit (TL)"
              description="Kasadaki nakdi say ve gerçek tutarı gir"
              value={counted}
              onChange={(v) => setCounted(Number(v) || 0)}
              size="lg"
              min={0}
              decimalScale={2}
              thousandSeparator=","
            />

            <Alert color={varianceMinor === 0 ? "green" : "red"} icon={<IconAlertTriangle size={16} />}>
              <Group justify="space-between">
                <Text fw={600}>
                  {varianceMinor === 0 ? "Kasa denk" : varianceMinor < 0 ? "Kasada NOKSAN" : "Kasada FAZLA"}
                </Text>
                <MoneyDisplay minor={varianceMinor} fw={700} />
              </Group>
            </Alert>

            <Button size="lg" color="red" fullWidth loading={closeShift.isPending} onClick={handleClose}>
              Kasayı Kapat
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}

function CloseRow({ label, minor }: { label: string; minor: number }) {
  return (
    <Group justify="space-between">
      <Text c="dimmed" size="sm">{label}</Text>
      <MoneyDisplay minor={minor} size="sm" />
    </Group>
  );
}
